/**
 * Payment Accounts API
 * RTK Query endpoints for payment account management
 */

import { baseApi } from "./baseApi";

/**
 * Payment Account interface matching backend
 */
export interface PaymentAccount {
	id: string;
	organization_id?: string;
	account_name: string;
	account_number: string;
	bank_name: string;
	is_active: boolean;
	notes?: string;
	created_at: string;
	updated_at: string;
}

/**
 * Payment account list response interface
 */
export interface PaymentAccountListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: PaymentAccount[];
}

/**
 * Create payment account request interface
 */
export interface PaymentAccountData {
	account_name: string;
	account_number: string;
	bank_name: string;
	is_active?: boolean;
	notes?: string;
}

/**
 * Update payment account request interface
 */
export interface PaymentAccountUpdateData {
	account_name?: string;
	account_number?: string;
	bank_name?: string;
	is_active?: boolean;
	notes?: string;
}

/**
 * Payment Accounts API endpoints
 */
export const paymentAccountsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Create a new payment account (admin only)
		 */
		createPaymentAccount: builder.mutation<PaymentAccount, PaymentAccountData>({
			query: (data) => ({
				url: "/payment-accounts/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["PaymentAccounts"],
		}),

		/**
		 * List all payment accounts (admin only)
		 */
		listPaymentAccounts: builder.query<
			PaymentAccountListResponse,
			{
				is_active?: boolean;
				search?: string;
				ordering?: string;
				page?: number;
				page_size?: number;
			}
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				if (params.is_active !== undefined)
					queryParams.append("is_active", params.is_active.toString());
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());

				const queryString = queryParams.toString();
				return {
					url: queryString
						? `/payment-accounts/?${queryString}`
						: `/payment-accounts/`,
					method: "GET",
				};
			},
			providesTags: ["PaymentAccounts"],
		}),

		/**
		 * Get payment account details by ID (admin only)
		 */
		getPaymentAccount: builder.query<PaymentAccount, string>({
			query: (id) => ({
				url: `/payment-accounts/${id}`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "PaymentAccounts", id }],
		}),

		/**
		 * Update a payment account (admin only)
		 */
		updatePaymentAccount: builder.mutation<
			PaymentAccount,
			{ id: string; data: PaymentAccountUpdateData }
		>({
			query: ({ id, data }) => ({
				url: `/payment-accounts/${id}`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "PaymentAccounts", id },
				"PaymentAccounts",
			],
		}),

		/**
		 * Delete a payment account (admin only)
		 */
		deletePaymentAccount: builder.mutation<void, string>({
			query: (id) => ({
				url: `/payment-accounts/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["PaymentAccounts"],
		}),
	}),
});

/**
 * Public endpoint to get active payment accounts (for users)
 */
export const paymentAccountsApiPublic = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List active payment accounts (public endpoint)
		 */
		listPublicPaymentAccounts: builder.query<PaymentAccountListResponse, void>({
			query: () => ({
				url: "/payment-accounts/public/",
				method: "GET",
			}),
		}),
	}),
});

export const { useListPublicPaymentAccountsQuery } = paymentAccountsApiPublic;

export const {
	useCreatePaymentAccountMutation,
	useListPaymentAccountsQuery,
	useLazyListPaymentAccountsQuery,
	useGetPaymentAccountQuery,
	useLazyGetPaymentAccountQuery,
	useUpdatePaymentAccountMutation,
	useDeletePaymentAccountMutation,
} = paymentAccountsApi;
